import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { 
  Building2, 
  Plus,
  Pencil,
  Trash2,
  Users,
  ClipboardList,
  JapaneseYen,
  MapPin,
  Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacilitiesPage() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState(null);
  const [facilityStats, setFacilityStats] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    municipality: '津市',
    services: []
  });

  const serviceOptions = ['日中一時支援', '移動支援', '地域生活支援'];

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API}/facilities`);
      setFacilities(response.data);
      
      // 各事業所の統計を取得
      const stats = {};
      for (const facility of response.data) {
        try {
          const statsRes = await axios.get(`${API}/facilities/${facility.id}/stats`);
          stats[facility.id] = statsRes.data;
        } catch (e) {
          stats[facility.id] = { clients_count: 0, staff_count: 0, records_count: 0, current_month_billing: 0 };
        }
      }
      setFacilityStats(stats);
    } catch (error) {
      console.error('事業所データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceChange = (service, checked) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, service]
        : prev.services.filter(s => s !== service)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFacility) {
        await axios.put(`${API}/facilities/${editingFacility.id}`, formData);
      } else {
        await axios.post(`${API}/facilities`, formData);
      }
      setDialogOpen(false);
      setEditingFacility(null);
      setFormData({ name: '', address: '', phone: '', municipality: '津市', services: [] });
      fetchFacilities();
    } catch (error) {
      console.error('保存に失敗しました', error);
    }
  };

  const handleEdit = (facility) => {
    setEditingFacility(facility);
    setFormData({
      name: facility.name,
      address: facility.address,
      phone: facility.phone,
      municipality: facility.municipality,
      services: facility.services || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (facilityId) => {
    try {
      await axios.delete(`${API}/facilities/${facilityId}`);
      fetchFacilities();
    } catch (error) {
      console.error('削除に失敗しました', error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingFacility(null);
    setFormData({ name: '', address: '', phone: '', municipality: '津市', services: [] });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="facilities-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            事業所管理
          </h1>
          <p className="text-muted-foreground mt-1">
            登録事業所数: {facilities.length}件
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-active" data-testid="add-facility-button">
              <Plus size={18} className="mr-2" />
              新規事業所
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {editingFacility ? '事業所を編集' : '新規事業所登録'}
              </DialogTitle>
              <DialogDescription>
                事業所情報を入力してください
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">事業所名 *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="○○事業所"
                  required
                  data-testid="facility-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">住所 *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="三重県津市..."
                  required
                  data-testid="facility-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">電話番号 *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="059-XXX-XXXX"
                  required
                  data-testid="facility-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="municipality">自治体</Label>
                <Input
                  id="municipality"
                  value={formData.municipality}
                  onChange={(e) => handleChange('municipality', e.target.value)}
                  placeholder="津市"
                  data-testid="facility-municipality-input"
                />
              </div>
              <div className="space-y-2">
                <Label>提供サービス</Label>
                <div className="flex flex-wrap gap-4">
                  {serviceOptions.map(service => (
                    <div key={service} className="flex items-center gap-2">
                      <Checkbox
                        id={`service-${service}`}
                        checked={formData.services.includes(service)}
                        onCheckedChange={(checked) => handleServiceChange(service, checked)}
                        data-testid={`facility-service-${service}`}
                      />
                      <Label htmlFor={`service-${service}`} className="cursor-pointer text-sm">
                        {service}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  キャンセル
                </Button>
                <Button type="submit" data-testid="facility-submit-button">
                  {editingFacility ? '更新' : '登録'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* 事業所一覧 */}
      {facilities.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-12 text-center">
            <Building2 size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">事業所が登録されていません</p>
            <Button 
              className="mt-4" 
              variant="outline"
              onClick={() => setDialogOpen(true)}
            >
              最初の事業所を登録する
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {facilities.map((facility) => {
            const stats = facilityStats[facility.id] || {};
            return (
              <Card key={facility.id} className="card-surface" data-testid={`facility-card-${facility.id}`}>
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 size={20} className="text-primary" weight="duotone" />
                      {facility.name}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {facility.municipality}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="icon" 
                      variant="ghost"
                      onClick={() => handleEdit(facility)}
                      data-testid={`edit-facility-${facility.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-facility-${facility.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>事業所を削除しますか？</AlertDialogTitle>
                          <AlertDialogDescription>
                            この操作は取り消せません。{facility.name}のすべてのデータが削除されます。
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>キャンセル</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(facility.id)}>
                            削除する
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* 基本情報 */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin size={14} />
                      {facility.address}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone size={14} />
                      {facility.phone}
                    </div>
                  </div>
                  
                  {/* サービス */}
                  <div className="flex flex-wrap gap-1">
                    {facility.services?.map((service, idx) => (
                      <span key={idx} className="badge-success text-xs">
                        {service}
                      </span>
                    ))}
                  </div>
                  
                  {/* 統計 */}
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-primary" />
                      <span className="text-sm">
                        <span className="font-medium">{stats.clients_count || 0}</span>
                        <span className="text-muted-foreground ml-1">利用者</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ClipboardList size={16} className="text-primary" />
                      <span className="text-sm">
                        <span className="font-medium">{stats.records_count || 0}</span>
                        <span className="text-muted-foreground ml-1">記録</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 col-span-2">
                      <JapaneseYen size={16} className="text-primary" />
                      <span className="text-sm">
                        <span className="font-medium">{formatCurrency(stats.current_month_billing || 0)}</span>
                        <span className="text-muted-foreground ml-1">今月</span>
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
